import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { getLobIdByName } from "@/shared/common/static";
import type { RootState } from "@/shared/redux/store/store";

export type QuoteLobKey = "motor" | "health" | "travel";

export type LobQuoteAddonCache = {
  fetchKey: string | null;
  quoteList: any[];
  filterQuoteList: any[];
  originalQuoteList: any[];
  selectedAddOns: string[];
};

export type QuoteAndAddonState = Record<QuoteLobKey, LobQuoteAddonCache>;

const createEmptyLobCache = (): LobQuoteAddonCache => ({
  fetchKey: null,
  quoteList: [],
  filterQuoteList: [],
  originalQuoteList: [],
  selectedAddOns: [],
});

const initialState: QuoteAndAddonState = {
  motor: createEmptyLobCache(),
  health: createEmptyLobCache(),
  travel: createEmptyLobCache(),
};

export const getLobKeyFromLobId = (lobId?: number): QuoteLobKey | null => {
  switch (lobId) {
    case getLobIdByName("motor"):
      return "motor";
    case getLobIdByName("health"):
      return "health";
    case getLobIdByName("travel"):
      return "travel";
    default:
      return null;
  }
};

const quoteAndAddonSlice = createSlice({
  name: "quoteAndAddon",
  initialState,
  reducers: {
    saveLobQuoteAndAddonCache(
      state,
      action: PayloadAction<{
        lobKey: QuoteLobKey;
        fetchKey: string;
        quoteList: any[];
        filterQuoteList: any[];
        originalQuoteList: any[];
        selectedAddOns: string[];
      }>
    ) {
      const { lobKey, fetchKey, quoteList, filterQuoteList, originalQuoteList, selectedAddOns } =
        action.payload;

      state[lobKey] = {
        fetchKey,
        quoteList: [...quoteList],
        filterQuoteList: [...filterQuoteList],
        originalQuoteList: [...originalQuoteList],
        selectedAddOns: [...selectedAddOns],
      };
    },
    clearLobQuoteAndAddonCache(state, action: PayloadAction<QuoteLobKey>) {
      state[action.payload] = createEmptyLobCache();
    },
    clearAllQuoteAndAddonCaches() {
      return initialState;
    },
  },
});

export const {
  saveLobQuoteAndAddonCache,
  clearLobQuoteAndAddonCache,
  clearAllQuoteAndAddonCaches,
} = quoteAndAddonSlice.actions;

export const selectLobQuoteAndAddonCache = (state: RootState, lobKey: QuoteLobKey) =>
  state.quoteAndAddon[lobKey];

export const selectHasValidQuoteAndAddonCache = (
  state: RootState,
  lobKey: QuoteLobKey,
  fetchKey: string
) => {
  const cache = state.quoteAndAddon[lobKey];
  return cache.fetchKey === fetchKey && cache.quoteList.length > 0;
};

export default quoteAndAddonSlice.reducer;
